/** I13 - the typed view of an old save that migrations read through.
 *
 * A `Migration` is handed `unknown` on purpose: the shape it reads is not the
 * shape the code has any more, so typing its input as `GameState` would be a
 * lie that stops compiling the first time the root changes. But "unknown" is
 * not the same as "untyped": every rung asks the same four questions - is this
 * an envelope, where is the state, what is in this entity map, how do I stamp
 * the result - and before this file each one answered them with its own private
 * copy of `isRecord`.
 *
 * So the raw tree gets a vocabulary instead. It is deliberately made of plain
 * records rather than of the current interfaces: `readSave` knows a save has a
 * `state`, and nothing more, which is exactly what remains true across every
 * future version.
 */

/** A parsed object node. Everything below it is still `unknown`. */
export type Tree = Readonly<Record<string, unknown>>;

export const isRecord = (v: unknown): v is Tree =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/** A record-valued field, or null if it is absent or is not a record. */
export function recordAt(t: Tree, key: string): Tree | null {
  const v = t[key];
  return isRecord(v) ? v : null;
}

/** A string-valued field, or null. Used for `contentVersion`, which is the one
 *  envelope field a migration ever has to move by hand. */
export function stringAt(t: Tree, key: string): string | null {
  const v = t[key];
  return typeof v === "string" ? v : null;
}

/**
 * A save envelope split into the two halves a migration touches. `file` is the
 * envelope, `state` the game inside it; both are plain records, so a rung reads
 * whichever fields its own version had.
 */
export interface SaveTree {
  readonly file: Tree;
  readonly state: Tree;
}

/** The envelope, or null when the input is not one. A rung that returns null
 *  from here reports "could not read this file" rather than corrupting it. */
export function readSave(raw: unknown): SaveTree | null {
  if (!isRecord(raw)) return null;
  const state = recordAt(raw, "state");
  return state === null ? null : { file: raw, state };
}

/**
 * Rewrite one entity map - `players`, `heroes`, `towns`, `armies`, `objects` -
 * entry by entry, returning the new state. Ids are visited in sorted order (I3)
 * so a migration cannot become order-dependent by accident. `null` from the
 * callback, or a map that is not a record, aborts the rung.
 */
export function mapEntities(
  state: Tree, mapKey: string, f: (entity: Tree, id: string) => Tree | null,
): Tree | null {
  const map = recordAt(state, mapKey);
  if (map === null) return null;
  const next: Record<string, unknown> = {};
  for (const id of Object.keys(map).sort()) {
    const entity = map[id];
    if (!isRecord(entity)) return null;
    const rewritten = f(entity, id);
    if (rewritten === null) return null;
    next[id] = rewritten;
  }
  return { ...state, [mapKey]: next };
}

/**
 * The common rung: a new field on a nested shape, with the same default for
 * every entity. Existing values are kept, so re-running a rung is harmless.
 */
export function addFieldToEntities(
  state: Tree, mapKey: string, field: string, value: unknown,
): Tree | null {
  return mapEntities(state, mapKey, (entity) =>
    Object.prototype.hasOwnProperty.call(entity, field) ? entity : { ...entity, [field]: value });
}

/**
 * Stamp the result. The version travels twice - on the envelope, where a reader
 * looks first, and inside the state, where the kernel reads it - and
 * `validateSaveTree` refuses a file where the two disagree, so no rung is
 * allowed to write only one of them.
 */
export function writeSave(save: SaveTree, state: Tree, to: number): Tree {
  return {
    ...save.file,
    formatVersion: to,
    state: { ...state, formatVersion: to },
  };
}
